import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminAiDatasetDetail,
  type AdminAiDatasetListResult,
  type AdminAiDatasetStatsResult,
  type AdminAiDatasetSummaryResult,
  type AdminAiFeature,
  type AdminAiLabel,
  type AdminUserCoachRow,
} from "api/adminApi";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  HelpTip,
  Input,
  NUMERIC,
  SectionHeader,
  Select,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
} from "pages/admin/ui";
import {
  shiftDateInputValue,
  todayInputValue,
} from "pages/admin/user/userFormat";
import DatasetRowModal from "./DatasetRowModal";
import LoadRules from "./LoadRules";
import {
  FEATURES,
  LABEL_HELP,
  LABEL_TEXT,
  LABEL_TONE,
  LABELS,
  changeSummary,
  editRatioLabel,
  formatOccurredAt,
} from "./aiFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";
const PAGE_SIZE = 50;
// 새 행 확인 주기 — 목록을 통째로 다시 받지 않고 건수만 묻는다
const POLL_MS = 60_000;
// 영역이 둘 이상일 때만 영역 컬럼이 붙는다
const columnCount = (allFeatures: boolean) => (allFeatures ? 7 : 6);

const ALL_FEATURES = FEATURES.map(({ key }) => key);

const FEATURE_LABEL = Object.fromEntries(
  FEATURES.map(({ key, label }) => [key, label])
) as Record<AdminAiFeature, string>;

// 기간 프리셋 — 앰버서더 성과와 같은 모양. custom 은 시작·종료일을 직접 입력받는다.
type DatePreset = "today" | "7d" | "30d" | "custom";

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: "today", label: "오늘" },
  { key: "7d", label: "7일" },
  { key: "30d", label: "30일" },
  { key: "custom", label: "직접" },
];

// 프리셋 → [시작일, 종료일] (yyyy-MM-dd). 프리셋은 모두 오늘까지라 끝을 열어 둔다 —
// 오늘로 고정하면 자정이 지난 뒤 새 행이 목록·폴링에서 빠진다. custom 은 기존 입력값을 유지하므로 다루지 않는다.
const presetRange = (preset: DatePreset): [string, string] => {
  const today = todayInputValue();
  const shift = (days: number) => shiftDateInputValue(today, days) ?? today;
  switch (preset) {
    case "7d":
      return [shift(-6), ""];
    case "30d":
      return [shift(-29), ""];
    default:
      return [today, ""];
  }
};

type Filters = {
  label: AdminAiLabel | "";
  adminEdited: "" | "true" | "false";
  coachId: string;
  from: string;
  to: string;
};

const defaultFilters = (): Filters => ({
  label: "",
  adminEdited: "",
  coachId: "",
  // 처음엔 오늘 적재분부터 본다
  from: presetRange("today")[0],
  to: presetRange("today")[1],
});

// 커서 = 직전 페이지 마지막 행. null 이면 첫 페이지
type Cursor = { occurredAt: string; id: number } | null;

/**
 * AI 관리 — 데이터셋. 코치가 AI 출력을 쓰거나 고치거나 버린 액션 하나가 한 행으로 실시간 적재된다 (서버 AiDatasetHelper).
 * 여기서는 행을 훑고, 행을 열어 정답 보완과 SFT·DPO·RL 설정을 한다. 필수 검수 흐름은 없다.
 *
 * 목록은 최신순 커서 페이징이다 — 서버가 OFFSET 없이 인덱스 범위로 한 페이지만 읽는다.
 * 전체 건수는 첫 페이지에서만 받아 들고 있고, 영역별 건수는 처음과 새 행을 불러올 때만 따로 묻는다.
 * 영역은 칩으로 여러 개를 고른다. 기본은 셋 다 선택(= 전 영역)이고, 마지막 하나는 끌 수 없다.
 * 영역이 둘 이상일 때만 영역 컬럼을 보인다.
 */
export default function DatasetSection() {
  // 고른 영역 (FEATURES 순서). 셋 다면 전 영역
  const [features, setFeatures] = useState<AdminAiFeature[]>(ALL_FEATURES);
  const allFeatures = features.length === ALL_FEATURES.length;
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [datePreset, setDatePreset] = useState<DatePreset>("today");
  // 지나온 페이지의 커서 — 마지막 것이 지금 페이지. "이전" 은 하나 꺼낸다
  const [cursors, setCursors] = useState<Cursor[]>([null]);
  const [data, setData] = useState<AdminAiDatasetListResult | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<AdminAiDatasetStatsResult | null>(null);
  const [summaryData, setSummaryData] =
    useState<AdminAiDatasetSummaryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCount, setNewCount] = useState(0);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [coaches, setCoaches] = useState<AdminUserCoachRow[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);

  const page = cursors.length - 1;
  const cursor = cursors[page];

  const filterParam = useMemo(
    () => ({
      feature: allFeatures ? undefined : features,
      label: filters.label || undefined,
      adminEdited:
        filters.adminEdited === "" ? undefined : filters.adminEdited === "true",
      coachId: filters.coachId ? Number(filters.coachId) : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    }),
    [allFeatures, features, filters]
  );

  // 탭·필터·페이지를 빠르게 바꾸면 이전 요청이 나중에 도착할 수 있다 — 마지막 요청의 응답만 반영한다 (폴링도 같은 번호를 본다)
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.aiDatasetList({
        ...filterParam,
        cursorOccurredAt: cursor?.occurredAt,
        cursorId: cursor?.id,
        size: PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return;
      if (res.code === 0 && res.result) {
        setData(res.result);
        if (res.result.totalCount != null) setTotalCount(res.result.totalCount);
        setNewCount(0);
        setLoadedAt(new Date());
      } else {
        setData(null);
        setError(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setData(null);
      const status = (e as AxiosError).response?.status;
      setError(
        status === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [filterParam, cursor]);

  useEffect(() => {
    load();
  }, [load]);

  // 영역 칩 건수 — 영역·필터·페이지와 무관해서 처음과 새 행을 불러올 때만 묻는다
  const summarySeq = useRef(0);
  const loadSummary = useCallback(async () => {
    const seq = ++summarySeq.current;
    try {
      const res = await adminApi.aiDatasetSummary();
      if (seq === summarySeq.current && res.code === 0 && res.result)
        setSummaryData(res.result);
    } catch (e) {
      // 칩 건수만 비어 있을 뿐 목록은 쓸 수 있다
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // 라벨 통계 카드 — 목록 필터 중 라벨만 빼고 따른다 (라벨 분포를 보는 카드라서). 페이지를 넘길 때는 묻지 않는다
  const { label: _label, ...statsParam } = filterParam;
  const statsKey = JSON.stringify(statsParam);
  const statsSeq = useRef(0);
  const loadStats = useCallback(async () => {
    const seq = ++statsSeq.current;
    try {
      const res = await adminApi.aiDatasetStats(JSON.parse(statsKey));
      if (seq === statsSeq.current && res.code === 0 && res.result)
        setStats(res.result);
    } catch (e) {
      // 카드만 비어 있을 뿐 목록은 쓸 수 있다
    }
  }, [statsKey]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.userCoachList();
        if (res.code === 0) setCoaches(res.result ?? []);
      } catch (e) {
        // 코치 필터만 비어 있을 뿐 목록은 쓸 수 있다
      }
    })();
  }, []);

  // 새 행 폴링 — 첫 페이지를 보고 있을 때만, 건수만 묻는다. 기준은 지금 받은 행 중 가장 큰 id.
  const topId = data?.rows.reduce((max, r) => Math.max(max, r.id), 0) ?? 0;
  useEffect(() => {
    if (page !== 0) return;
    const timer = window.setInterval(async () => {
      const seq = requestSeq.current;
      try {
        const res = await adminApi.aiDatasetNewCount({
          ...filterParam,
          afterId: topId,
        });
        if (seq !== requestSeq.current) return;
        if (res.code === 0) setNewCount(res.result ?? 0);
      } catch (e) {
        // 다음 주기에 다시 묻는다
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [page, topId, filterParam]);

  // 영역 칩 — 여러 개를 켜고 끈다. 마지막 하나는 끌 수 없다
  const toggleFeature = (key: AdminAiFeature) => {
    if (features.length === 1 && features[0] === key) return;
    setFeatures((prev) =>
      prev.includes(key)
        ? prev.filter((f) => f !== key)
        : ALL_FEATURES.filter((f) => f === key || prev.includes(f))
    );
    setCursors([null]);
  };

  const applyDatePreset = (next: DatePreset) => {
    setDatePreset(next);
    if (next === "custom") return;
    const [from, to] = presetRange(next);
    setFilters((prev) => ({ ...prev, from, to }));
    setCursors([null]);
  };

  const changeFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCursors([null]);
  };

  // 새 행 불러오기 — 첫 페이지에서만 보이므로 목록을 다시 받고 요약도 새로 묻는다
  const reloadNewRows = () => {
    load();
    loadSummary();
    loadStats();
  };

  const nextPage = () => {
    const last = data?.rows[data.rows.length - 1];
    if (!data?.hasNext || !last) return;
    setCursors((prev) => [
      ...prev,
      { occurredAt: last.occurredAt, id: last.id },
    ]);
  };

  const prevPage = () =>
    setCursors((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));

  // 모달에서 저장하면 목록의 보완·학습 사용 표시만 바꾼다 (다시 불러오면 스크롤·페이지가 흔들린다)
  const onSaved = (detail: AdminAiDatasetDetail) =>
    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              r.id === detail.id
                ? {
                    ...r,
                    adminEdited: detail.adminOutput != null,
                    sftEnabled: detail.sftEnabled,
                    dpoEnabled: detail.dpoEnabled,
                    rlEnabled: detail.rlEnabled,
                    split: detail.split,
                  }
                : r
            ),
          }
        : prev
    );

  const rows = data?.rows ?? [];
  const lastPage = Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1);

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="데이터셋"
        count={`${totalCount.toLocaleString()}행`}
      >
        {/* 기간 프리셋 — 앰버서더 성과와 같은 모양 */}
        <div className="flex rounded-md border border-slate-300 bg-white p-0.5">
          {DATE_PRESETS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`rounded px-2.5 py-1 text-sm font-medium transition-colors ${
                datePreset === item.key
                  ? "bg-[#0F55A7] text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              onClick={() => applyDatePreset(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {datePreset === "custom" && (
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              fullWidth={false}
              className="w-36"
              aria-label="시작일"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => changeFilter("from", e.target.value)}
            />
            <span className="text-sm text-slate-400">~</span>
            <Input
              type="date"
              fullWidth={false}
              className="w-36"
              aria-label="종료일"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => changeFilter("to", e.target.value)}
            />
          </div>
        )}
      </SectionHeader>

      <LoadRules />

      {/* 영역 칩 — 복수 선택, 기본 셋 다(전 영역) */}
      <div className="flex flex-wrap items-center gap-2">
        {FEATURES.map(({ key, label }) => {
          const on = features.includes(key);
          return (
            <button
              type="button"
              key={key}
              aria-pressed={on}
              onClick={() => toggleFeature(key)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                on
                  ? "border-[#0F55A7] bg-[#DCEFFA] font-medium text-[#0F55A7]"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
              }`}
            >
              {label}
              <span
                className={`text-xs ${NUMERIC} ${on ? "text-[#0F55A7]/70" : "text-slate-400"}`}
              >
                {(summaryData?.featureCounts[key] ?? 0).toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap items-end gap-2">
        <Select
          sizing="md"
          fullWidth={false}
          className="w-36"
          aria-label="라벨"
          value={filters.label}
          onChange={(e) =>
            changeFilter("label", e.target.value as Filters["label"])
          }
        >
          <option value="">라벨 전체</option>
          {LABELS.map((l) => (
            <option key={l} value={l}>
              {LABEL_TEXT[l]}
            </option>
          ))}
        </Select>
        <Select
          fullWidth={false}
          className="w-40"
          aria-label="관리자 보완"
          value={filters.adminEdited}
          onChange={(e) =>
            changeFilter(
              "adminEdited",
              e.target.value as Filters["adminEdited"]
            )
          }
        >
          <option value="">보완 여부 전체</option>
          <option value="true">보완함</option>
          <option value="false">보완 안 함</option>
        </Select>
        <Select
          fullWidth={false}
          className="w-40"
          aria-label="코치"
          value={filters.coachId}
          onChange={(e) => changeFilter("coachId", e.target.value)}
        >
          <option value="">전체 코치</option>
          {coaches.map((c) => (
            <option key={c.coachId} value={c.coachId}>
              {c.name}
            </option>
          ))}
        </Select>
        <span className="ml-auto pb-2 text-xs text-slate-400">
          {loadedAt &&
            `마지막 갱신 ${loadedAt.toLocaleTimeString("ko-KR", { hour12: false })}`}
        </span>
      </div>

      {/* 라벨 통계 — 위 필터(라벨 제외) 기준 */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards(stats).map(({ title, help, value, sub }) => (
          <Card key={title} className="px-4 py-3">
            <p className="flex items-center text-xs text-slate-500">
              {title}
              <HelpTip text={help} />
            </p>
            <p
              className={`mt-1 text-xl font-semibold text-slate-900 ${NUMERIC}`}
            >
              {value}
            </p>
            <p className={`mt-0.5 text-xs text-slate-400 ${NUMERIC}`}>{sub}</p>
          </Card>
        ))}
      </div>

      {newCount > 0 && (
        <button
          type="button"
          onClick={reloadNewRows}
          className="rounded-md border border-[#0F55A7]/20 bg-[#DCEFFA] px-3 py-2 text-left text-sm font-medium text-[#0F55A7] hover:bg-[#cfe7f7]"
        >
          새 행 {newCount.toLocaleString()}건 · 눌러서 불러오기
        </button>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Table>
        <THead>
          <TH>일시</TH>
          {features.length > 1 && <TH>영역</TH>}
          <TH help={LABEL_HELP}>라벨</TH>
          <TH>AI 출력</TH>
          <TH>코치 저장값</TH>
          <TH>코치</TH>
          <TH>관리자</TH>
        </THead>
        <tbody>
          {loading && !data ? (
            <TSkeleton colSpan={columnCount(features.length > 1)} />
          ) : rows.length === 0 ? (
            <TEmpty colSpan={columnCount(features.length > 1)}>
              조건에 맞는 행이 없어요
            </TEmpty>
          ) : (
            rows.map((row) => {
              const change = changeSummary(row);
              return (
                <TRow key={row.id} className="cursor-pointer">
                  <TD
                    muted
                    className={NUMERIC}
                    onClick={() => setOpenId(row.id)}
                  >
                    {formatOccurredAt(row.occurredAt)}
                  </TD>
                  {features.length > 1 && (
                    <TD
                      className="whitespace-nowrap"
                      onClick={() => setOpenId(row.id)}
                    >
                      {FEATURE_LABEL[row.feature]}
                    </TD>
                  )}
                  <TD onClick={() => setOpenId(row.id)}>
                    <span className="flex items-center gap-1.5">
                      <Badge tone={LABEL_TONE[row.label]}>
                        {LABEL_TEXT[row.label]}
                      </Badge>
                      <span className={`text-xs text-slate-400 ${NUMERIC}`}>
                        {row.label === "edited"
                          ? editRatioLabel(row.editRatio)
                          : ""}
                      </span>
                    </span>
                  </TD>
                  <TD
                    className="max-w-[360px]"
                    onClick={() => setOpenId(row.id)}
                  >
                    <HoverText text={change.ai} className="text-slate-500">
                      {change.ai || (
                        <span className="text-slate-300">(AI 없음)</span>
                      )}
                    </HoverText>
                  </TD>
                  <TD
                    className="max-w-[360px]"
                    onClick={() => setOpenId(row.id)}
                  >
                    {/* 그대로면 AI 출력과 같아서 다시 보이지 않는다 */}
                    {row.label === "as_is" ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <HoverText text={change.coach}>
                        {change.coach == null ? (
                          <span className="text-slate-300">(안 씀)</span>
                        ) : (
                          change.coach || (
                            <span className="text-slate-300">(비움)</span>
                          )
                        )}
                      </HoverText>
                    )}
                  </TD>
                  <TD muted onClick={() => setOpenId(row.id)}>
                    {row.coachName ?? row.coachId ?? ""}
                  </TD>
                  <TD onClick={() => setOpenId(row.id)}>
                    <span className="flex items-center gap-1.5">
                      {row.adminEdited && <Badge tone="info">보완됨</Badge>}
                      {row.sftEnabled && <Badge tone="ok">SFT</Badge>}
                      {row.dpoEnabled && <Badge tone="ok">DPO</Badge>}
                      {row.rlEnabled && <Badge tone="ok">RL</Badge>}
                    </span>
                  </TD>
                </TRow>
              );
            })
          )}
        </tbody>
      </Table>

      <div className="flex items-center justify-end gap-2">
        <span className={`text-sm text-slate-500 ${NUMERIC}`}>
          {page + 1} / {Math.max(lastPage, page) + 1}
        </span>
        <Button size="sm" disabled={page === 0 || loading} onClick={prevPage}>
          이전
        </Button>
        <Button
          size="sm"
          disabled={!data?.hasNext || loading}
          onClick={nextPage}
        >
          다음
        </Button>
      </div>

      {openId != null && (
        <DatasetRowModal
          id={openId}
          rowIds={rows.map((r) => r.id)}
          onNavigate={setOpenId}
          onSaved={onSaved}
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

// 말줄임된 셀 — 마우스를 올리면 전체 메시지를 바로 보여준다.
// 표 컨테이너가 overflow-x-auto 라 absolute 툴팁은 잘려서, 셀 위치를 재서 fixed 로 띄운다.
// 화면 아래쪽 셀이면 위로 띄운다.
function HoverText({
  text,
  className = "",
  children,
}: {
  text: string | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<CSSProperties | null>(null);
  return (
    <p
      className={`truncate ${className}`}
      onMouseEnter={(e) => {
        if (!text) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setPos(
          rect.bottom > window.innerHeight / 2
            ? { left: rect.left, bottom: window.innerHeight - rect.top + 4 }
            : { left: rect.left, top: rect.bottom + 4 }
        );
      }}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && (
        <span
          role="tooltip"
          style={{ position: "fixed", ...pos }}
          className="z-50 max-h-80 w-[480px] max-w-[90vw] overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-slate-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg"
        >
          {text}
        </span>
      )}
    </p>
  );
}

// 통계 카드 — 미사용률·DPO 쌍 후보(라벨), 학습 사용 행·정답 없는 행(학습 준비). 그대로 채택률·수정률은 잠시 뺐다 (서버는 계속 내려준다). 라벨 카드의 분모는 AI 출력이 있던 최종 결과 행(자동적 사고 항목 저장 제외), 학습 준비 카드는 필터에 걸린 전체 행
// 카드 설명 — 계산식 한 줄 + 필터 범위
const STATS_FILTER_HELP = "필터: 기간·영역·코치·보완 적용, 라벨 미적용.";

const statCards = (stats: AdminAiDatasetStatsResult | null) => {
  const total = stats?.aiOfferedCount ?? 0;
  const rate = (count: number | undefined) =>
    stats == null || total === 0
      ? "–"
      : `${Math.round(((count ?? 0) / total) * 100)}%`;
  const of = (count: number | undefined) =>
    stats == null
      ? ""
      : `${(count ?? 0).toLocaleString()} / ${total.toLocaleString()}행`;
  return [
    {
      title: "미사용률",
      help: `unused ÷ AI 출력이 있는 최종 행. 최종 행 = field_save 제외 (confirm·delete·send·deliver·comment_delete). ${STATS_FILTER_HELP}`,
      value: rate(stats?.unusedCount),
      sub: of(stats?.unusedCount),
    },
    {
      title: "DPO 쌍 후보",
      help: `최종 행 중 label ≠ as_is 이고 AI 출력·코치 값이 모두 있는 행. rejected = AI 출력, chosen = 코치 값. ${STATS_FILTER_HELP}`,
      value:
        stats == null ? "–" : `${stats.dpoCandidateCount.toLocaleString()}행`,
      sub: "",
    },
    {
      title: "학습 사용 행",
      help: `sft_enabled · dpo_enabled · rl_enabled 가 켜진 행 수. 분모 = 필터에 걸린 전체 행 (field_save 포함). ${STATS_FILTER_HELP}`,
      value:
        stats == null
          ? "–"
          : `SFT ${stats.sftCount.toLocaleString()} · DPO ${stats.dpoCount.toLocaleString()} · RL ${stats.rlCount.toLocaleString()}`,
      sub:
        stats == null
          ? ""
          : `전체 ${stats.filteredCount.toLocaleString()}행 중`,
    },
    {
      title: "정답 없는 행",
      help: `coach_output·admin_output 이 모두 없는 행 — SFT 정답이 없어 샘플이 안 만들어진다. 분모 = 필터에 걸린 전체 행. ${STATS_FILTER_HELP}`,
      value: stats == null ? "–" : `${stats.noAnswerCount.toLocaleString()}행`,
      sub:
        stats == null
          ? ""
          : `전체 ${stats.filteredCount.toLocaleString()}행 중`,
    },
  ];
};
